import * as React from 'react';
import { Eye, EyeOff } from 'lucide-react';

import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { cn } from '../../lib/utils';

export interface SecretInputProps extends Omit<React.ComponentProps<'input'>, 'type'> {
  /**
   * Render the built-in reveal toggle. Set `false` when the surface owns its
   * own affordance — for example one that has to fetch the stored value from
   * the server before it can show anything.
   */
  reveal?: boolean;
  /**
   * Controlled masking. Omit to let the component own the state; pass it
   * alongside `reveal={false}` to drive masking from an external control.
   */
  revealed?: boolean;
  onRevealedChange?: (revealed: boolean) => void;
  /**
   * What the toggle calls this field: `Show {revealLabel}` / `Hide
   * {revealLabel}`. Name the field when the surface has several secrets or
   * repeats rows, so the button's accessible name stays unambiguous. Avoid
   * reusing the field's own label — a toggle named the same as the input it
   * sits in makes both unaddressable by name.
   */
  revealLabel?: string;
  /** Class for the wrapper that positions the toggle. */
  containerClassName?: string;
}

/**
 * A masked value that is **not** an account password: an API key, an auth
 * token, a webhook secret, an env var.
 *
 * `type="password"` is the only reliable way to mask text — CSS masking
 * (`-webkit-text-security`) degrades to plain text where it is unsupported,
 * which is a worse failure than the one it solves. But the attribute also
 * tells browsers and password managers "this is the user's password for this
 * site", which is false here and has consequences: managers offer to fill or
 * save the field, and a saved account password autofilled into an API key box
 * gets submitted and stored as a third-party credential.
 *
 * So this keeps the masking and drops the claim — `passwordManager="ignore"`
 * applies every vendor opt-out. Use it for any secret that is not the
 * credential the user signs in with; use a plain `<Input type="password"
 * autoComplete="current-password">` for those.
 */
export function SecretInput({
  reveal = true,
  revealed,
  onRevealedChange,
  revealLabel = 'secret',
  className,
  containerClassName,
  ...props
}: SecretInputProps) {
  const [internalRevealed, setInternalRevealed] = React.useState(false);
  const isControlled = revealed !== undefined;
  const isRevealed = isControlled ? revealed : internalRevealed;

  function toggle() {
    const next = !isRevealed;
    if (!isControlled) setInternalRevealed(next);
    onRevealedChange?.(next);
  }

  const input = (
    <Input
      type={isRevealed ? 'text' : 'password'}
      passwordManager="ignore"
      className={cn(reveal && 'pr-10', className)}
      {...props}
    />
  );

  if (!reveal) return input;

  return (
    <div className={cn('relative', containerClassName)}>
      {input}
      <Button
        type="button"
        variant="ghost"
        size="icon-xs"
        onClick={toggle}
        // A field the user cannot manage should not carry a live control that
        // uncovers its value.
        disabled={props.disabled}
        // Skipped in the tab order: the field and its submit button are the
        // path through the form, and masking is a mouse-reach affordance.
        tabIndex={-1}
        aria-label={isRevealed ? `Hide ${revealLabel}` : `Show ${revealLabel}`}
        className="absolute right-2 top-1/2 -translate-y-1/2"
      >
        {isRevealed ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </Button>
    </div>
  );
}
